import { Card, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card';
import type { ClientRecord } from '@/api/clients';
import {
    useClientCustomFieldDefs,
    type CustomFieldDef,
    type CustomFieldValue,
} from '@/api/customFields';
import { fmtDob } from '../clientHelpers';
import { DetailList } from './DetailList';

/**
 * Reads custom_fields off the client record (JSONB map keyed by def id) and
 * renders each active def with its label, in sortOrder. Archived defs whose
 * values are still stored on the record are intentionally not shown here —
 * practices archive a field when they want it off the record; values keyed
 * to archived defs remain in the DB so no data is lost if the def is
 * restored later (see POST /api/custom-fields/:subjectType/:id/restore).
 */
export function ClientCustomFieldsTab({ client: c }: { client: ClientRecord }) {
    const { data: defs, isLoading } = useClientCustomFieldDefs();
    const values = (c.customFields || {}) as Record<string, CustomFieldValue>;

    if (isLoading) {
        return (
            <Card>
                <CardHeader><CardTitle>Custom Fields</CardTitle></CardHeader>
                <p className="text-sm text-text-subtle">Loading…</p>
            </Card>
        );
    }

    if (!defs || defs.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <div>
                        <CardTitle>Custom Fields</CardTitle>
                        <CardSubtitle>No custom fields configured for this practice.</CardSubtitle>
                    </div>
                </CardHeader>
                <p className="text-sm text-text-subtle">
                    Practice admins can add custom fields from Settings.
                </p>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader><CardTitle>Custom Fields</CardTitle></CardHeader>
            <DetailList
                items={defs.map((def) => ({
                    label: def.label,
                    value: renderValue(def, values[def.id] ?? null),
                }))}
            />
        </Card>
    );
}

function renderValue(def: CustomFieldDef, v: CustomFieldValue): React.ReactNode {
    if (v === null || v === undefined || v === '') return '';
    switch (def.type) {
        case 'checkbox':
            return v ? 'Yes' : 'No';
        case 'date':
            return fmtDob(typeof v === 'string' ? v : null);
        case 'multiselect':
            return Array.isArray(v) && v.length ? v.join(', ') : '';
        case 'number':
            return typeof v === 'number' ? String(v) : '';
        case 'textarea':
            return (
                <span className="whitespace-pre-wrap">{typeof v === 'string' ? v : ''}</span>
            );
        case 'select':
        case 'text':
        default:
            return typeof v === 'string' ? v : '';
    }
}
